import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import {
  CHECKBOX_EMPTY_ICON,
  CHECKBOX_FULL_ICON,
  CHECKBOX_PARTIAL_ICON,
} from '../asset-list/constants/funnel-header.constants';

@Component({
  selector: 'mtm-checkbox',
  templateUrl: './mtm-checkbox.component.html',
  styleUrls: ['./mtm-checkbox.component.scss'],
  standalone: true,
  imports: [CommonModule, NgOptimizedImage],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MtmCheckboxComponent),
      multi: true,
    },
  ],
})
export class MtmCheckboxComponent implements ControlValueAccessor, AfterViewInit, OnChanges {
  @Input() checked: boolean = false;
  @Input() disabled: boolean = false;
  @Input() indeterminate: boolean = false;
  @Input() name: string = '';
  @Input() id: string = '';
  @Input() label: string = '';
  @Input() ariaLabel: string = '';
  @Input() customClass: string = '';

  @ViewChild('checkboxInput', { static: false }) checkboxInput?: ElementRef<HTMLInputElement>;

  @Output() checkedChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() change: EventEmitter<Event> = new EventEmitter<Event>();

  public onCheckboxChange(event: Event): void {
    if (this.disabled) {
      event.preventDefault();
      return;
    }

    const target = event.target as HTMLInputElement;
    if (this.indeterminate) {
      this.indeterminate = false;
      this.checked = true;
      this.updateIndeterminateState();
    } else {
      this.checked = target.checked;
    }
    this.onChange(this.checked);
    this.onTouched();
    this.checkedChange.emit(this.checked);
    this.change.emit(event);
  }

  public onCheckboxClick(event: Event): void {
    if (this.disabled) {
      event.preventDefault();
      return;
    }
  }

  public onKeyDown(event: Event): void {
    const e = event as KeyboardEvent;
    if (e.key === 'Enter' || e.key === ' ') {
      event.preventDefault();
      if (!this.disabled) {
        if (this.indeterminate) {
          this.indeterminate = false;
          this.checked = true;
          this.updateIndeterminateState();
        } else {
          this.checked = !this.checked;
        }
        this.onChange(this.checked);
        this.onTouched();
        this.checkedChange.emit(this.checked);
        this.change.emit(event);
      }
    }
  }

  public writeValue(value: boolean): void {
    this.checked = value || false;
  }

  public getCheckboxIcon(): string {
    if (this.indeterminate) {
      return CHECKBOX_PARTIAL_ICON;
    }
    return this.checked ? CHECKBOX_FULL_ICON : CHECKBOX_EMPTY_ICON;
  }

  public registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public ngAfterViewInit(): void {
    this.updateIndeterminateState();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['indeterminate'] && !changes['indeterminate'].firstChange) {
      this.updateIndeterminateState();
    }
  }

  private onChange = (value: boolean) => {};

  private onTouched = () => {};

  private updateIndeterminateState(): void {
    if (this.checkboxInput?.nativeElement) {
      this.checkboxInput.nativeElement.indeterminate = this.indeterminate;
    }
  }
}
